import React, { useEffect } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  UilTimes,
  UilCheckCircle,
  UilExclamationTriangle,
  UilInfoCircle
} from '@iconscout/react-unicons';

interface HVAToastProps {
  show: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  delay?: number;
  autohide?: boolean;
  position?:
    | 'top-start'
    | 'top-center'
    | 'top-end'
    | 'middle-start'
    | 'middle-center'
    | 'middle-end'
    | 'bottom-start'
    | 'bottom-center'
    | 'bottom-end';
}

const HVAToast: React.FC<HVAToastProps> = ({
  show,
  onClose,
  title,
  message,
  variant = 'info',
  delay = 7000,
  autohide = true,
  position = 'top-center'
}) => {
  useEffect(() => {
    if (show && autohide) {
      const timer = setTimeout(() => {
        onClose();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [show, autohide, delay, onClose]);

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return <UilCheckCircle className="text-success me-2" size={20} />;
      case 'error':
        return (
          <UilExclamationTriangle className="text-danger me-2" size={20} />
        );
      case 'warning':
        return (
          <UilExclamationTriangle className="text-warning me-2" size={20} />
        );
      case 'info':
      default:
        return <UilInfoCircle className="text-info me-2" size={20} />;
    }
  };

  const getTitle = () => {
    if (title) return title;
    switch (variant) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'info':
      default:
        return 'Information';
    }
  };

  return (
    <ToastContainer
      position={position}
      className="p-3"
      style={{ zIndex: 9999 }}
    >
      <Toast
        onClose={onClose}
        show={show}
        delay={delay}
        autohide={autohide}
        className="shadow-lg"
      >
        <Toast.Header closeButton={false}>
          <div className="d-flex align-items-center flex-grow-1">
            {getIcon()}
            <strong className="me-auto">{getTitle()}</strong>
          </div>
          <Button
            className="ms-2 p-0 bg-transparent border-0"
            onClick={onClose}
          >
            <UilTimes className="fs-7 text-body-emphasis" />
          </Button>
        </Toast.Header>
        <Toast.Body className="text-body-emphasis">{message}</Toast.Body>
      </Toast>
    </ToastContainer>
  );
};

export default HVAToast;
